import { formatEUR } from "../lib/format";

/**
 * Compact summary of filtered invoice list.
 * Shows: count, total Soll, total Ist, total Offen, status breakdown.
 *
 * Props:
 *  - items: filtered list
 *  - kind: "avgs" or "fbw" (determines amount calculation)
 */
function amountOf(it, kind) {
  if (kind === "fbw") {
    return (it.num_installments || 0) * (it.installment_amount || 0);
  }
  return it.total_amount || 0;
}

export default function FilterSummary({ items, kind = "avgs" }) {
  const stats = items.reduce(
    (acc, it) => {
      const total = amountOf(it, kind);
      acc.count += 1;
      acc.soll += total;
      const isPaid = it.manually_paid || it.status === "paid";
      const isPartial = it.status === "partial";
      if (isPaid) {
        acc.ist += total;
        acc.paidCount += 1;
      } else if (isPartial) {
        acc.ist += (it.paid_amount || 0);
        acc.offen += total - (it.paid_amount || 0);
        acc.partialCount += 1;
      } else {
        acc.offen += total;
        acc.openCount += 1;
      }
      return acc;
    },
    { count: 0, soll: 0, ist: 0, offen: 0, paidCount: 0, openCount: 0, partialCount: 0 }
  );

  if (stats.count === 0) return null;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 md:p-4" data-testid="filter-summary">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <div className="label-eyebrow">Treffer</div>
          <div className="font-display text-xl font-bold mt-0.5">{stats.count}</div>
          <div className="text-[10px] text-[#94A3B8]">
            {stats.paidCount} bezahlt · {stats.partialCount > 0 ? `${stats.partialCount} teilweise · ` : ""}{stats.openCount} offen
          </div>
        </div>
        <div className="h-10 w-px bg-[#E5E7EB] hidden md:block"></div>
        <div>
          <div className="label-eyebrow">Summe (Soll)</div>
          <div className="font-display text-xl font-bold text-[#1F242E] mt-0.5">{formatEUR(stats.soll)}</div>
        </div>
        <div className="h-10 w-px bg-[#E5E7EB] hidden md:block"></div>
        <div>
          <div className="label-eyebrow">Bezahlt (Ist)</div>
          <div className="font-display text-xl font-bold text-[#0AA09E] mt-0.5">{formatEUR(stats.ist)}</div>
        </div>
        <div className="h-10 w-px bg-[#E5E7EB] hidden md:block"></div>
        <div>
          <div className="label-eyebrow">Offen</div>
          <div className={`font-display text-xl font-bold mt-0.5 ${stats.offen > 0 ? "text-[#FF6B5C]" : "text-[#5B6371]"}`}>
            {formatEUR(stats.offen)}
          </div>
        </div>
        {stats.soll > 0 && (
          <div className="flex-1 min-w-[180px]">
            <div className="label-eyebrow mb-1">Bezahlt-Anteil</div>
            <div className="relative h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-[#0AA09E] rounded-full"
                style={{ width: `${Math.min(100, (stats.ist / stats.soll) * 100)}%` }}></div>
            </div>
            <div className="text-[10px] text-[#5B6371] mt-1">
              {Math.round((stats.ist / stats.soll) * 100)}% bezahlt
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
